import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { clsx } from "clsx";

import { Select as RadixSelect } from "radix-ui";
import { Check, ChevronDown, ChevronUp, Gem, Search } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

export type SelectProps<T> = PropsWithChildren<{
  options: T[];
  getOptionLabel: (opt: T) => React.ReactNode;
  getOptionDescription?: (opt: T) => React.ReactNode;
  getOptionValue: (opt: T) => string;
  getOptionDisabled?: (opt: T) => boolean;
  getOptionTracking?: (opt: T) => string;
  /** Icon rendered beside each dropdown item, but NOT projected into the trigger. */
  getOptionIcon?: (opt: T) => React.ReactNode;
  /** Content rendered after an option's label inside the dropdown item. */
  getOptionEndContent?: (opt: T) => React.ReactNode;
  /** Overrides how the selected option is rendered inside the closed trigger. */
  renderSelectedValue?: (opt: T) => React.ReactNode;
  contentProps?: React.ComponentProps<typeof RadixSelect.Content>;
  triggerProps?: React.ComponentProps<typeof RadixSelect.Trigger>;
  rootProps?: React.ComponentProps<typeof RadixSelect.Root>;
  disabled?: boolean;
  disabledIcon?: React.ReactNode;
  triggerTracking?: string;
  error?: string;
  footer?: React.ReactNode;
  /** Renders a persistent label inside the trigger, e.g. "Status :" */
  insetLabel?: string;
  /**
   * When true, renders a search input inside the dropdown that filters the
   * visible options by matching against `getOptionSearchText` (or the string
   * form of `getOptionLabel` when that prop is omitted).
   */
  searchable?: boolean;
  /** Placeholder for the in-dropdown search input. */
  searchPlaceholder?: string;
  /**
   * Text used to match against the search query when `searchable` is true.
   * Required for options whose label is a `ReactNode`; when omitted, the
   * label is coerced to a string.
   */
  getOptionSearchText?: (opt: T) => string;
  /** Message rendered inside the dropdown when the search matches no options. */
  searchEmptyMessage?: React.ReactNode;
}>;

const SCROLL_BUTTON_CLASS =
  "bg-surface-default text-textSubtle flex h-6 cursor-default items-center justify-center";

interface SelectItemProps {
  children: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  endContent?: React.ReactNode;
  value: string;
  disabled?: boolean;
  selectTracking?: string;
  disabledIcon?: React.ReactNode;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  (
    {
      children,
      description,
      icon,
      endContent,
      disabled,
      disabledIcon,
      selectTracking,
      ...props
    },
    forwardedRef,
  ) => {
    return (
      <RadixSelect.Item
        {...props}
        ref={forwardedRef}
        disabled={disabled}
        data-track={selectTracking}
        className={clsx(
          "text-body-sm flex items-center justify-between px-4 py-2 last-of-type:rounded-b-md hover:!border-none hover:outline-0 focus:outline-0",
          disabled
            ? "cursor-not-allowed text-gray-400"
            : "cursor-pointer hover:bg-blue-50 focus:bg-blue-50",
        )}
      >
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="text-gray-500" aria-hidden>
                {icon}
              </span>
            )}
            <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
            {endContent}
          </div>
          {description && (
            <span className="text-body-xs text-textSubtle mt-0.5">
              {description}
            </span>
          )}
        </div>
        {disabled && disabledIcon ? (
          disabledIcon
        ) : (
          <RadixSelect.ItemIndicator>
            <Check className="text-blue-500" size={16} />
          </RadixSelect.ItemIndicator>
        )}
      </RadixSelect.Item>
    );
  },
);

export function Select<T>({
  contentProps,
  triggerProps,
  options,
  getOptionLabel,
  getOptionDescription,
  getOptionValue,
  getOptionDisabled,
  getOptionTracking,
  getOptionIcon,
  getOptionEndContent,
  renderSelectedValue,
  rootProps,
  disabled = false,
  disabledIcon = <Gem size={14} className="text-gray-400" />,
  triggerTracking,
  error,
  footer,
  insetLabel,
  searchable = false,
  searchPlaceholder = "Search…",
  getOptionSearchText,
  searchEmptyMessage = "No matches",
}: SelectProps<T>) {
  const { contentRef, onOpenStateChange, currentSide } = useSideState();
  const selectedOption = options.find(
    (option) => getOptionValue(option) === rootProps?.value,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleOpenStateChange = useCallback(
    (open: boolean) => {
      rootProps?.onOpenChange?.(open);
      onOpenStateChange(open);
      if (open) {
        // Radix's Select.Content has no onOpenAutoFocus escape hatch (unlike
        // its other primitives), so we grab focus ourselves once the portaled
        // content — and the search input inside it — has mounted.
        if (searchable) {
          requestAnimationFrame(() => searchInputRef.current?.focus());
        }
      } else {
        setSearchQuery("");
      }
    },
    [onOpenStateChange, rootProps, searchable],
  );

  const visibleOptions = useMemo(() => {
    if (!searchable) return options;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const searchText =
        getOptionSearchText?.(opt) ??
        (() => {
          const label = getOptionLabel(opt);
          return typeof label === "string" ? label : String(label ?? "");
        })();
      return searchText.toLowerCase().includes(q);
    });
  }, [searchable, searchQuery, options, getOptionSearchText, getOptionLabel]);

  const handleValueChange = useCallback(
    (value: string) => {
      const baseTrackingId = triggerTracking?.trim();
      const selectedOption = options.find(
        (opt) => getOptionValue(opt) === value,
      );
      const optionTrackingId = selectedOption
        ? getOptionTracking?.(selectedOption)?.trim()
        : undefined;
      const trackingValue =
        optionTrackingId ||
        (baseTrackingId ? `${baseTrackingId}-${value}` : undefined);

      if (trackingValue && typeof window !== "undefined" && window.freshpaint) {
        window.freshpaint.track("Select Option Clicked", {
          tracking_id: trackingValue,
          selected_value: value,
        });
      }
      rootProps?.onValueChange?.(value);
    },
    [triggerTracking, options, getOptionValue, getOptionTracking, rootProps],
  );

  return (
    <RadixSelect.Root
      {...rootProps}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenStateChange}
      key={rootProps?.value}
      disabled={disabled}
    >
      <div className="space-y-1">
        <RadixSelect.Trigger
          {...triggerProps}
          aria-label="Options"
          data-track={triggerTracking}
          className={twMerge(
            clsx(
              "border-dividerBold text-textDefault bg-surface-default relative inline-flex w-64 cursor-pointer items-center justify-between gap-2 overflow-hidden rounded-md border p-4 whitespace-nowrap transition outline-none focus:border-blue-500 data-[state=open]:border-blue-500",
              {
                "data-[state=open]:border-b-dividerBold rounded-b-md data-[state=open]:rounded-b-none":
                  currentSide === "bottom",
                "data-[state=open]:border-t-dividerBold rounded-t-md data-[state=open]:rounded-t-none":
                  currentSide === "top",
              },
              disabled && "cursor-not-allowed opacity-50",
              error && "text-textError border-red-500",
              triggerProps?.className,
            ),
          )}
        >
          {insetLabel && (
            <span className="text-label-sm text-textSubtle shrink-0">
              {insetLabel}
            </span>
          )}
          <RadixSelect.Value
            placeholder={triggerProps?.placeholder || "Select an option"}
            className="min-w-0 truncate"
          >
            {selectedOption && renderSelectedValue?.(selectedOption)}
          </RadixSelect.Value>
          <RadixSelect.Icon className="text-textSubtle shrink-0">
            <ChevronDown size={20} />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        {error && <div className="text-body-sm text-textError">{error}</div>}
      </div>

      <RadixSelect.Portal>
        <RadixSelect.Content
          {...contentProps}
          ref={contentRef}
          className={twMerge(
            clsx(
              "data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn text-textDefault bg-surface-default z-popover w-[var(--radix-select-trigger-width)] overflow-hidden border border-blue-500",
              {
                "rounded-b-md": currentSide === "bottom",
                "rounded-t-md": currentSide === "top",
              },
              contentProps?.className,
            ),
          )}
          style={{
            maxHeight:
              "min(20rem, var(--radix-select-content-available-height))",
            ...contentProps?.style,
          }}
          position="popper"
        >
          {searchable && (
            <div className="border-b border-gray-200 p-2">
              <div className="relative">
                <Search
                  size={14}
                  aria-hidden
                  className="text-textSubtle pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
                />
                <input
                  ref={searchInputRef}
                  type="search"
                  role="searchbox"
                  aria-label="Search options"
                  autoComplete="off"
                  spellCheck={false}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    // Let Radix Select handle arrow keys / Enter / Escape for
                    // list navigation, but keep character input inside the
                    // search box (Radix's default typeahead would otherwise
                    // steal it).
                    if (
                      event.key !== "ArrowDown" &&
                      event.key !== "ArrowUp" &&
                      event.key !== "Enter" &&
                      event.key !== "Escape" &&
                      event.key !== "Tab"
                    ) {
                      event.stopPropagation();
                    }
                  }}
                  placeholder={searchPlaceholder}
                  className="text-body-sm text-textDefault placeholder:text-textSubtle bg-surface-default w-full rounded border border-gray-200 py-1 pr-2 pl-7 outline-none focus:border-blue-500"
                />
              </div>
            </div>
          )}
          <RadixSelect.ScrollUpButton className={SCROLL_BUTTON_CLASS}>
            <ChevronUp size={20} />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport>
            <RadixSelect.Group>
              {visibleOptions.map((option: T) => (
                <SelectItem
                  key={getOptionValue(option)}
                  value={getOptionValue(option)}
                  description={getOptionDescription?.(option)}
                  icon={getOptionIcon?.(option)}
                  endContent={getOptionEndContent?.(option)}
                  disabled={getOptionDisabled?.(option)}
                  disabledIcon={disabledIcon}
                  selectTracking={
                    getOptionTracking?.(option) ??
                    (triggerTracking
                      ? `${triggerTracking}-${getOptionValue(option)}`
                      : undefined)
                  }
                >
                  {getOptionLabel(option)}
                </SelectItem>
              ))}
            </RadixSelect.Group>
            {searchable && visibleOptions.length === 0 && (
              <div className="text-body-sm text-textSubtle px-4 py-2">
                {searchEmptyMessage}
              </div>
            )}
            {footer && (
              <>
                <RadixSelect.Separator className="mx-2 my-1 h-px bg-gray-200" />
                <div className="text-body-xs px-4 py-2 font-medium text-gray-500">
                  {footer}
                </div>
              </>
            )}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className={SCROLL_BUTTON_CLASS}>
            <ChevronDown size={20} />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

type Side = RadixSelect.SelectContentProps["side"];

function useSideState() {
  const [currentSide, setCurrentSide] = useState<Side>("bottom");
  const contentRef = useRef<HTMLDivElement>(null);

  const onOpenStateChange = useCallback((open: boolean) => {
    if (open) {
      setTimeout(() => {
        const contentElement = contentRef.current;
        if (contentElement) {
          setCurrentSide((contentElement.dataset.side || "bottom") as Side);
        }
      }, 0);
    }
  }, []);

  return { onOpenStateChange, currentSide, contentRef };
}
